import type { WalkListItem } from './api';

/** Leading house number of an address, or null when it doesn't start with digits. */
export function houseNumber(address: string | null): number | null {
  if (!address) return null;
  const m = address.match(/^\s*(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/** Street name = address with the leading house number stripped. */
export function streetOf(address: string | null): string {
  if (!address) return 'Unknown';
  return address.replace(/^\d+\s*/, '').trim() || 'Unknown';
}

/**
 * Group walk-list items by street, sort each street's rows by numeric house
 * number (non-numeric addresses sort last), and sort the street groups
 * alphabetically. Returns [street, rows][] ready to render.
 */
export function groupAndSort(items: WalkListItem[]): [string, WalkListItem[]][] {
  const m = new Map<string, WalkListItem[]>();
  for (const it of items) {
    const k = streetOf(it.address);
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(it);
  }
  for (const rows of m.values()) {
    rows.sort((a, b) => {
      const na = houseNumber(a.address);
      const nb = houseNumber(b.address);
      // Numeric addresses first, ordered ascending; non-numeric (null) last.
      if (na === null && nb === null) return 0;
      if (na === null) return 1;
      if (nb === null) return -1;
      return na - nb;
    });
  }
  return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

const SUPPORT_LABELS: Record<number, string> = {
  1: 'Strong opp', 2: 'Lean opp', 3: 'Undecided', 4: 'Lean supp', 5: 'Strong supp',
};

const RESULT_LABELS: Record<string, string> = {
  not_home: 'Not home',
  refused: 'Refused',
  moved: 'Moved',
  wrong_address: 'Wrong addr',
  no_response: 'No response',
  wrong_number: 'Wrong number',
  sent: 'Sent',
  returned_undeliverable: 'Returned undeliverable',
  signed_petition: 'Signed petition',
};

/**
 * Last-contact cell label. Prefer a support label when present; otherwise,
 * for a result-only contact (not_home / refused / etc.), show a short result
 * label so the row isn't blank.
 */
export function lastContactLabel(
  supportLevel: number | null,
  contactResult: string | null,
): string {
  if (supportLevel != null) return SUPPORT_LABELS[supportLevel] ?? '';
  if (contactResult) return RESULT_LABELS[contactResult] ?? '';
  return '';
}
